// Orb.jsx — Crystal ball component (Three.js)
//
// Architecture:
//   [div.orb-wrapper]          ← CSS breathing animation
//     [div.orb-text-layer]     ← z-index: 1 — daemon text BEHIND the glass
//     [canvas]                 ← z-index: 2 — Three.js glass sphere + particles + swirls
//     [div.orb-shadow]         ← ground shadow
//
// The canvas renders with alpha: true so the transparent parts of the glass
// let the text layer show through the interior. The specular highlights and
// reflections on the glass surface sit ON TOP of the text, creating the
// illusion that the text is submerged inside a physical glass sphere.

import { useRef, useEffect, useCallback } from 'react';
import { OrbRenderer } from './OrbCanvas.js';
import { startShatter } from './OrbShatter.js';
import { getMaterial } from './materials.js';
import './orb.css';

function orbStateName(n) {
  if (n < 5)  return 'nascent';
  if (n < 10) return 'stirring';
  if (n < 15) return 'awakening';
  if (n < 20) return 'bound';
  return 'sovereign';
}

export default function Orb({
  text,
  textPhase,
  uiPhase,
  exchangeCount,
  material,
  materialBlend,
  onShatterComplete,
}) {
  const canvasRef  = useRef(null);
  const wrapperRef = useRef(null);
  const renderer   = useRef(null);
  const rafId      = useRef(null);
  const shattering = useRef(false);
  const lastTs     = useRef(null);

  // ── Init Three.js renderer ─────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    const r = new OrbRenderer(canvas);
    renderer.current = r;

    const diam = wrapperRef.current.getBoundingClientRect().width;
    r.resize(Math.round(diam));

    const loop = (ts) => {
      const dt = lastTs.current ? ts - lastTs.current : 16;
      lastTs.current = ts;
      r.update(ts, dt);
      rafId.current = requestAnimationFrame(loop);
    };
    rafId.current = requestAnimationFrame(loop);

    const obs = new ResizeObserver(entries => {
      const { width } = entries[0].contentRect;
      if (width > 0) renderer.current?.resize(Math.round(width));
    });
    obs.observe(wrapperRef.current);

    return () => {
      cancelAnimationFrame(rafId.current);
      obs.disconnect();
      r.destroy();
    };
  }, []);

  // ── Sync orb state ─────────────────────────────────────────────────────────
  useEffect(() => {
    renderer.current?.setOrbState(orbStateName(exchangeCount));
  }, [exchangeCount]);

  // ── Sync material ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!material) return;
    renderer.current?.setMaterial(getMaterial(material));
  }, [material]);

  useEffect(() => {
    renderer.current?.setMaterialBlend(materialBlend || 0);
  }, [materialBlend]);

  // ── Shattering ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (uiPhase !== 'shattering' || shattering.current) return;
    shattering.current = true;
    cancelAnimationFrame(rafId.current);

    const diam = wrapperRef.current.getBoundingClientRect().width;
    const r = diam / 2;
    const mat = material ? getMaterial(material) : null;

    // Capture the Three.js canvas as the shatter snapshot source
    // OrbShatter expects two canvas refs (bottom/top) — pass the same one twice
    startShatter(
      canvasRef.current,
      canvasRef.current,
      wrapperRef.current,
      r, r, r,
      mat,
      onShatterComplete,
    );
  }, [uiPhase, material, onShatterComplete]);

  const stateClass = `orb-state-${orbStateName(exchangeCount)}`;
  const uiClass = `orb-ui-${uiPhase}`;

  // Compute scroll animation duration from text length for desktop
  const scrollDur = text
    ? Math.min(22, Math.max(8, text.trim().split(/\s+/).length * 0.35)) + 's'
    : '12s';

  return (
    <div
      ref={wrapperRef}
      className={`orb-wrapper ${stateClass} ${uiClass}`}
      role="img"
      aria-label="The daemon orb"
    >
      {/* Layer 1 — Text: sits BEHIND the glass canvas */}
      <div className="orb-text-layer" aria-hidden="true">
        {text && textPhase !== 'hidden' && (
          <p
            style={{ '--scroll-dur': scrollDur }}
            className={`orb-text orb-text-${textPhase}`}
          >
            {text}
          </p>
        )}
      </div>

      {/* Layer 2 — Three.js glass sphere (specular highlights on top of text) */}
      <canvas ref={canvasRef} className="orb-canvas orb-canvas-glass" />

      {/* Ground shadow */}
      <div className="orb-shadow" aria-hidden="true" />
    </div>
  );
}
